import React, { FC, useState } from 'react';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { FocusPointEditorProps, PhotoAlignmentX, PhotoAlignmentY } from './FocusPointEditor.types';
import {
  EventPageType,
  PhotoXPosition,
  PhotoYPosition,
  useSetPageContainerPhotoAlignmentMutation,
  useSetPhotoAlignmentMutation,
  useUploadPhotoFromUrlMutation,
  WebsitePagePhotoPresentationFragment,
} from '@graphql/generated';
import { CheckIcon } from '@assets/icons';
import { useFocusPointEditorTelemetry } from './FocusPointEditor.telemetry';
import { EditorLayout } from './EditorLayout';
import { findMatchingPageType } from './utils';
import { useFeatureValue } from '@shared/core/featureFlags';
import { FocusPoint } from '../FocusPoint/FocusPoint';
import { WebsitePhotoAlignment } from '@apps/guest/websiteHeader';

export const FocusPointEditor: FC<FocusPointEditorProps> = props => {
  const {
    handleOnCancel,
    imageData,
    pageType,
    pageId,
    pageIdsData,
    eventId,
    isLoadingPhoto,
    handleSaveUploadedPhoto,
    isUpdatingEventPhoto,
    updatePagePhotoPreview,
    uploadedImageData,
    uploadedImagePhotoId,
    onFocusPointChange,
    telemetryCategory,
    pageSlug,
    hideFocusPointControls,

    handleCloseClick,
    isDeletingPagePhoto,
    handleRemovePhoto,
    handleChangePhoto,
    handleCancelClick,
  } = props;
  const alignX = (imageData?.layout.alignX as PhotoAlignmentX) || 'center';
  const alignY = (imageData?.layout.alignY as PhotoAlignmentY) || 'center';

  const [focusPoint, setFocusPoint] = useState<WebsitePhotoAlignment>({ alignX, alignY });

  const isPhotoUploadFromCollectionEnabled = useFeatureValue('photoUploadFromCollectionEnabled').value === 'on';

  const { toast } = useToast();
  const { tq } = useTranslation('joykit');

  const telemetry = useFocusPointEditorTelemetry();

  const [uploadPhotoFromUrl, { loading: waitingUploadPhoto }] = useUploadPhotoFromUrlMutation();
  const [setPageContainerPhotoAlignment, { loading: waitingForUpdatePageContainer }] = useSetPageContainerPhotoAlignmentMutation({
    onCompleted: () => toast(tq`Photo settings updated successfully.`, { icon: <CheckIcon /> }),
    onError: () => toast(tq`Failed to update photo settings.`, { icon: <WarningTriangle /> }),
  });
  const [updatePhotoAlignment, { loading: isUpdatingEventPhotoAlignment }] = useSetPhotoAlignmentMutation({
    onCompleted: () => toast(tq`Photo settings updated successfully.`, { icon: <CheckIcon /> }),
    onError: () => toast(tq`Failed to update photo settings.`, { icon: <WarningTriangle /> }),
  });

  const isUpdatingPhotoAlignment = isUpdatingEventPhotoAlignment || waitingForUpdatePageContainer || waitingUploadPhoto;

  const handleFocusPointChange = (x: PhotoXPosition, y: PhotoYPosition) => {
    setFocusPoint({ alignX: x, alignY: y });
    onFocusPointChange && onFocusPointChange(x, y);
  };

  const handleSaveFocusPoint = async () => {
    try {
      await handleSaveUploadedPhoto();
      // Custom and Normal page photo have different end points, we have to maintain both in global level
      if (pageType === EventPageType.custom) {
        let mainPhotoId: string | null | undefined = null;

        // if the photo is uploaded from collection feature is enableed, then we don't need to upload the photo again, it is handled by the PhotoPicker component
        if (isPhotoUploadFromCollectionEnabled && uploadedImagePhotoId) {
          mainPhotoId = uploadedImagePhotoId;
        } else {
          if (uploadedImageData && imageData?.url && pageId && uploadedImageData.assetId) {
            const { data } = await uploadPhotoFromUrl({
              variables: {
                payload: {
                  assetId: uploadedImageData.assetId,
                  containerId: pageId,
                  url: imageData.url,
                },
              },
            });
            mainPhotoId = data?.uploadPhotoFromUrl?.id;
            telemetry.pagePhotoUpdated(telemetryCategory, 'Update', 'succeeded', { pageType: pageSlug, actualPageType: pageType });
          }
        }
        await setPageContainerPhotoAlignment({
          variables: {
            photoAlignmentPayload: {
              id: pageId ?? '',
              ...(mainPhotoId && { mainPhotoId }),
              ...(focusPoint && { mainPhotoAlignment: { x: focusPoint.alignX as PhotoXPosition, y: focusPoint.alignY as PhotoYPosition } }),
              mainPhotoPresentationLayout: 'center',
            },
          },
          refetchQueries: ['PageContainerBySlug', 'WebsiteInfo'],
          awaitRefetchQueries: true,
        }).then(info => {
          if (!info.errors) {
            telemetry.pagePhotoUpdated(telemetryCategory, 'FocusChanged', 'succeeded', {
              previousFocus: [(imageData?.layout.alignX as PhotoXPosition) || PhotoXPosition.center, (imageData?.layout.alignY as PhotoYPosition) || PhotoYPosition.center],
              currentFocus: focusPoint,
            });
            handleOnCancel();
            const mainPhoto = info.data?.updatePageContainer.mainPhoto;
            const mainPhotoAlignment = info.data?.updatePageContainer.mainPhotoAlignment;
            const photoPresentation =
              info.data?.updatePageContainer.mainPhotoPresentationLayout && info.data?.updatePageContainer.mainPhotoCropInfo
                ? ({
                    id: info.data.updatePageContainer.id,
                    presentationLayout: info.data.updatePageContainer.mainPhotoPresentationLayout,
                    cropInfo: info.data.updatePageContainer.mainPhotoCropInfo,
                  } as WebsitePagePhotoPresentationFragment)
                : null;
            if (updatePagePhotoPreview && mainPhoto && mainPhotoAlignment) {
              const assetId = uploadedImageData?.assetId || imageData?.assetId || mainPhoto.id;
              updatePagePhotoPreview(
                assetId,
                {
                  id: mainPhotoId || mainPhoto.id,
                  url: mainPhoto.url,
                  assetId,
                  height: mainPhoto.height,
                  width: mainPhoto.width,
                  layout: {
                    alignX: mainPhotoAlignment?.x,
                    alignY: mainPhotoAlignment?.y,
                  },
                },
                photoPresentation,
              );
            }
          }
        });
      } else {
        telemetry.pagePhotoUpdated(telemetryCategory, 'FocusChanged', 'succeeded', {
          previousFocus: [(imageData?.layout.alignX as PhotoXPosition) || PhotoXPosition.center, (imageData?.layout.alignY as PhotoYPosition) || PhotoYPosition.center],
          currentFocus: focusPoint,
        });
        const pageTypeToUse = findMatchingPageType(pageType) || pageType;
        const pageIdToUse = pageIdsData.find(page => page.type === pageTypeToUse)?.id || pageId;
        updatePhotoAlignment({
          variables: {
            eventId: eventId || '',
            photoAlignmentPayload: {
              page: pageTypeToUse,
              isMobile: false,
              alignment: {
                x: focusPoint.alignX as PhotoXPosition,
                y: focusPoint.alignY as PhotoYPosition,
              },
            },
            photoPresentationPayload: {
              pageId: pageIdToUse || '',
              presentationLayout: 'center',
            },
          },
        }).then(info => {
          if (!info.errors) {
            handleOnCancel();
            if (updatePagePhotoPreview && info.data?.setPhotoAlignment) {
              const photoPresentation =
                info.data.websiteCreateOrUpdatePagePhotoPresentation?.__typename === 'websiteCreateOrUpdatePagePhotoPresentationResponse'
                  ? info.data.websiteCreateOrUpdatePagePhotoPresentation.photoPresentation
                  : null;
              updatePagePhotoPreview(uploadedImageData?.assetId || info.data.setPhotoAlignment.assetId, info.data.setPhotoAlignment, photoPresentation);
            }
          }
        });
      }
    } catch (error) {
      toast(tq`Failed to update photo settings.`, { icon: <WarningTriangle /> });
    }
  };
  return (
    <EditorLayout
      onCloseButtonClick={handleCloseClick}
      isCloseButtonDisabled={isUpdatingPhotoAlignment || isUpdatingEventPhoto}
      content={() => (
        <FocusPoint
          previewBackgroundColor={props.previewBackgroundColor}
          previewBackgroundSize={props.previewBackgroundSize}
          imageData={imageData}
          focusPoint={focusPoint}
          onFocusPointChange={handleFocusPointChange}
          hideFocusPointControls={hideFocusPointControls}
        />
      )}
      note={hideFocusPointControls ? '' : tq`Focus point ensures your image displays well on all devices.`}
      isDeletePhotoButtonDisabled={isLoadingPhoto || isDeletingPagePhoto}
      isDeletePhotoButtonLoading={isDeletingPagePhoto}
      onDeletePhotoButtonClick={handleRemovePhoto}
      onReplacePhotoButtonClick={handleChangePhoto}
      isReplacePhotoButtonDisabled={isLoadingPhoto}
      onSavePhotoButtonClick={handleSaveFocusPoint}
      isSavePhotoButtonDisabled={isUpdatingPhotoAlignment || isUpdatingEventPhoto || isLoadingPhoto}
      isSavePhotoButtonLoading={isUpdatingPhotoAlignment || isUpdatingEventPhoto}
      onCancelButtonClick={handleCancelClick}
      isCancelButtonDisabled={isUpdatingPhotoAlignment || isUpdatingEventPhoto}
    />
  );
};
